/**
 * ResourceEditorSidebar
 * Displays resource publication status, metadata, publish toggle, and delete action.
 * Gaps addressed: G12-2 (publish/unpublish), G12-3 (delete), G12-4 (user attribution)
 */

import { Calendar, Clock, Trash2, CheckCircle, XCircle, User } from 'lucide-react';

type ResourceEditorSidebarProps = {
  isPublished: boolean;
  isEditMode: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  onTogglePublish?: () => void;
  onDelete?: () => void;
  isToggling?: boolean;
};

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export function ResourceEditorSidebar({
  isPublished,
  isEditMode,
  createdAt,
  updatedAt,
  createdBy,
  updatedBy,
  onTogglePublish,
  onDelete,
  isToggling = false,
}: ResourceEditorSidebarProps) {
  return (
    <div className="space-y-4" data-testid="editor-sidebar">
      {/* Status card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <span
            className={`h-2.5 w-2.5 rounded-full ${isPublished ? 'bg-green-500' : 'bg-amber-400'}`}
          />
          <span className="text-sm font-semibold text-gray-900">
            {isPublished ? 'Published' : 'Draft'}
          </span>
        </div>

        {isEditMode && onTogglePublish && (
          <button
            type="button"
            onClick={onTogglePublish}
            disabled={isToggling}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
              isPublished
                ? 'border border-amber-300 text-amber-700 hover:bg-amber-50'
                : 'border border-green-300 text-green-700 hover:bg-green-50'
            }`}
            aria-label={isPublished ? 'Unpublish' : 'Publish'}
          >
            {isPublished ? (
              <>
                <XCircle className="h-4 w-4" />
                {isToggling ? 'Unpublishing...' : 'Unpublish'}
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                {isToggling ? 'Publishing...' : 'Publish'}
              </>
            )}
          </button>
        )}
      </div>

      {/* Information card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
          Information
        </h3>
        <div className="space-y-4 text-sm">
          <div className="flex items-start gap-2.5">
            <Calendar className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-gray-500">Created</p>
              <p className="font-medium text-gray-900">
                {formatDate(createdAt)}
                {createdAt && (
                  <span className="text-gray-400 font-normal ml-1">({timeAgo(createdAt)})</span>
                )}
              </p>
              {(createdBy || isEditMode) && (
                <p className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                  <User className="h-3 w-3" />
                  By {createdBy || 'Admin'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-gray-500">Last update</p>
              <p className="font-medium text-gray-900">
                {formatDate(updatedAt)}
                {updatedAt && (
                  <span className="text-gray-400 font-normal ml-1">({timeAgo(updatedAt)})</span>
                )}
              </p>
              {(updatedBy || isEditMode) && (
                <p className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                  <User className="h-3 w-3" />
                  By {updatedBy || 'Admin'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete action */}
      {isEditMode && onDelete && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <button
            type="button"
            onClick={onDelete}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
            aria-label="Delete this resource"
          >
            <Trash2 className="h-4 w-4" />
            Delete this entry
          </button>
        </div>
      )}
    </div>
  );
}
